import React, { useMemo } from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { addUserNotificationToReadList } from '../../services/notification.service';
import * as action from './store/notification.action';
import noImage from '../../assets/images/no-image.png';
import { checkArray, shortenandHoverString, imageUrl } from '../../Components/helper';
import { useNotificationState } from './NotificationProvider';

function UserNotificationItem(props) {
  const host = imageUrl;
  const data = props?.notification;
  const dispatch = useDispatch();
  const { notState } = useNotificationState();
  const { t } = useTranslation('common');

  const redirectToNotificationUrl = async (notification) => {
    const moduleName = notification?.messageDetail?.module;
    const devOrderId = notification?.messageDetail?.devOrderId;
    let url = '/';
    switch (moduleName) {
      case 'Design':
        url = `${url}development-order/${devOrderId}/design-details/list`;
        window.open(url, '_blank');
        break;
      case 'DevOrder':
        url = `${url}development-order-overview`;
        window.open(url, '_blank');
        break;
      case 'BOM':
        url = `${url}development-order/${devOrderId}/bom`;
        window.open(url, '_blank');
        break;
      case 'PCPCron':
        url = `${url}product-cp`;
        window.open(url, '_blank');
        break;
      case 'Testing':
        url = `${url}development-order/${devOrderId}/testing/testing-requirements`;
        window.open(url, '_blank');
        break;
      case 'Measurement':
        url = `${url}development-order/${devOrderId}/measurement`;
        window.open(url, '_blank');
        break;
      case 'Sample':
        url = `${url}development-order/${devOrderId}/samples`;
        window.open(url, '_blank');
        break;
      case 'userIntruction':
        url = `${url}development-order/${devOrderId}/care-user-instruction`;
        window.open(url, '_blank');
        break;
      case 'ProductCosting':
        url = `${url}development-order/${devOrderId}/product-costing`;
        window.open(url, '_blank');
        break;
      case 'PO':
        url = `${url}development-order/${devOrderId}/purchase-orders`;
        window.open(url, '_blank');
        break;
      case 'product-dam':
        url = `${url}development-order/${devOrderId}/digital-assets-management`;
        window.open(url, '_blank');
        break;
      case 'Share-Document':
        url = `${url}documents`;
        window.open(url, '_blank');
        break;
      case 'DownloadRangeplanSheet':
        url = `${url}range-plan/logs`;
        window.open(url, '_blank');
        break;
      case 'DownloadPCPSheet':
        url = `${url}product-cp/logs`;
        window.open(url, '_blank');
        break;
      case 'DownloadPimSheet':
        url = `${url}pim-v2/downloads`;
        window.open(url, '_blank');
        break;
      default:
        break;
    }
  };

  const addToReadingList = async (notification, stopRedirect, event) => {
    event?.stopPropagation();
    await addUserNotificationToReadList(notification?.messageDetail?._id);
    const reqObj = {
      pageIndex: 1,
      pageSize: 20,
    };
    dispatch(action.fetchNotificationList(reqObj, t));
    if (!stopRedirect) {
      await redirectToNotificationUrl(notification);
    }
  };

  const isExpand = notState?.expandedItems?.includes(data?.messageDetail?._id);
  const filteredFields = Array.isArray(data?.fields) ? data?.fields?.filter((vl) => vl?.field !== 'Product Identifier') : [];
  const memoizedHistory = useMemo(() => {
    return filteredFields?.sort((a, b) => {
      if (a?.field < b?.field) return -1;
      if (a?.field > b?.field) return 1;
      return 0;
    });
  }, [filteredFields]);

  const formatAppliedDate = (message) => {
    const dateRegex = /<span>(.*?)<\/span>/;
    const match = message.match(dateRegex);
    if (match) {
      const date = match[1];
      const formattedDate = moment(date).format('DD-MM-YYYY');
      return message.replace(dateRegex, `<span>${formattedDate}<span>`);
    }
    return message;
  };

  const renderDesignImageSwatches = () => {
    const imageData = data?.imageData || data?.messageDetail?.messageMoreInfo;
    const hasImages = Array.isArray(imageData?.images) && imageData.images.length > 0;
    const maxSwatches = 4;
    const imagesToShow = imageData?.images?.slice(0, maxSwatches);
    const remainingCount = Math.max(0, imageData?.images?.length - maxSwatches);
    return (
      <div className="design-image-swatches mt-2">
        {hasImages && (
          <div className="d-flex align-items-center flex-wrap gap-2">
            {imagesToShow?.map((img, index) => {
              let imgUrl = null;
              if (img?.image && img.image.trim() !== '') {
                imgUrl = img.image.startsWith('http') ? img.image : `${host}/${img.image}`;
              }
              const isRemoved = imageData?.imageAction === 'Removed' && img?.imageIndex === imageData?.imageIndex;
              return (
                <div
                  key={`swatch-${index}`}
                  className={`design-swatch ${!imgUrl ? 'no-image' : ''} ${isRemoved ? 'removed' : ''}`}
                  title={imgUrl ? `Image ${img.imageIndex}` : `No Image ${img.imageIndex}`}
                >
                  {imgUrl ? (
                    <img
                      alt={`Design image ${img.imageIndex}`}
                      src={imgUrl}
                      onError={(e) => {
                        e.target.src = noImage;
                        e.target.parentElement.classList.add('no-image');
                      }}
                    />
                  ) : (
                    <div className="no-image-placeholder">
                      <span>No Image</span>
                    </div>
                  )}
                  {isRemoved && <div className="removed-overlay" />}
                </div>
              );
            })}
            {remainingCount > 0 ? (
              <div className="design-swatch more-count" title={`${remainingCount} more images`}>
                <span>+{remainingCount}</span>
              </div>
            ) : null}
          </div>
        )}
        {imageData?.imageAction && (
          <div className={`${imageData?.images?.length > 0 ? 'mt-2 ' : ''} image-action-info`}>
            <p>
              {imageData?.imageAction === 'Removed' && imageData?.imageIndex != null && `Image Removed: Image ${imageData?.imageIndex}`}
              {imageData?.imageAction === 'Replaced' &&
                `${imageData?.oldTitle || imageData?.title || 'Artboard'} Image ${imageData?.imageIndex || 1} Replaced.`}
              {imageData?.imageAction === 'Submitted' && `${imageData?.oldTitle || imageData?.title || 'Artboard'} image Submitted.`}
              {imageData?.imageAction === 'Annotated' && imageData?.imageIndex != null && `Image Annotated: Image ${imageData?.imageIndex}`}
              {imageData?.imageAction === 'Archived' && `Archived ${imageData?.title} in Design/Details.`}
              {imageData?.imageAction === 'Unarchived' && `Unarchived ${imageData?.title} in Design/Details.`}
              {imageData?.imageAction === 'Swapped' && `Images Swapped: ${imageData?.images?.length || 0} image(s)`}
              {imageData?.imageAction === 'Deleted' && imageData?.imageIndex != null
                ? `Deleted Image ${imageData?.imageIndex} in ${imageData?.title} Design/Details.`
                : imageData?.imageAction === 'Deleted' && `Deleted ${imageData?.title} in Design/Details.`}
              {imageData?.imageAction === 'Updated' && imageData?.updatedField && (
                <>
                  Updated Design/Details <b>{imageData.updatedField}</b>
                  {imageData?.oldValue && imageData?.newValue && (
                    <>
                      {' '}
                      from <b>{imageData?.oldValue}</b> to <b>{imageData?.newValue}</b>
                    </>
                  )}
                  {imageData?.imageIndex != null && <>: Image {imageData?.imageIndex}</>}
                </>
              )}
              {imageData?.isCommentUpdate && (
                <>
                  Updated Design/Details comment for <b>{imageData?.title}.</b>
                </>
              )}
              {imageData?.imageAction === 'Updated' &&
                !imageData?.updatedField &&
                imageData?.imageIndex != null &&
                `Image Updated: Image ${imageData?.imageIndex}`}
              {imageData?.imageAction === 'Duplicated' && `Duplicated Design with ${imageData?.images?.length || 0} image(s)`}
            </p>
          </div>
        )}
        {imageData?.title && !imageData?.isCommentUpdate && !imageData?.updatedField && (
          <div className="design-name-info mt-1">
            <p>
              <b>Name:</b> {imageData?.title}
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderMessageContent = () => {
    const module = data?.messageDetail?.module;
    const message = data?.messageDetail?.message;
    const date = data?.messageDetail?.messageMoreInfo?.date;

    if (module === 'Sample' && date !== '') {
      return <div dangerouslySetInnerHTML={{ __html: formatAppliedDate(message) }} />;
    }

    if (module === 'Design') {
      return renderDesignImageSwatches();
    }

    return <div dangerouslySetInnerHTML={{ __html: message }} />;
  };

  return (
    <ul className="notification-ul">
      <li className="notification-item" onClick={() => addToReadingList(data)}>
        <div className="notification-content">
          <p className="notification-username">
            <b>{data?.messageDetail?.userName}</b>
          </p>
          {renderMessageContent()}
          {isExpand ? (
            <>
              {data?.messageDetail?.module === 'PCPCron' ? (
                <p className="my-2">
                  {t('NOTIFICATION_NOT_FOUND.ON_FOLLOWING')} <b>{memoizedHistory?.length}</b> {t('NOTIFICATION_NOT_FOUND.COLUMS')}
                </p>
              ) : (
                <p className="my-2">
                  {t('NOTIFICATION_NOT_FOUND.AS_WELL_AS')} <b>{memoizedHistory?.length}</b> {t('NOTIFICATION_NOT_FOUND.OTHER_TASKS')}
                </p>
              )}
              <ul>
                {memoizedHistory?.map((its) => {
                  if (its?.field !== 'Product Identifier') {
                    if (data?.messageDetail?.module === 'BOM' && (its?.field === 'Colourway/Artwork' || its?.field === 'Colourway') && its?.value !== '') {
                      const newColorAry = JSON.parse(its?.value);
                      if (newColorAry?.length > 0) {
                        return (
                          <div key={`Colour-${data?.messageDetail?._id}`}>
                            <li key={`Colour-${data?.messageDetail?._id}`}>
                              <div dangerouslySetInnerHTML={{ __html: its?.msg }} />
                            </li>
                            {newColorAry.map((item, index) => (
                              <div key={item.parentKey}>
                                <b>
                                  {index + 1}. {item?.parentKey}
                                </b>
                                <div>
                                  {item?.childKey?.map((child) => (
                                    <div key={child}> - {child}</div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      }
                      return (
                        <li key={its?.field}>
                          <div dangerouslySetInnerHTML={{ __html: its?.msg }} />
                        </li>
                      );
                    }

                    if (data?.messageDetail?.module === 'Measurement' && its?.field === 'Weight/Volume' && its?.value !== '') {
                      const weightVolumePairs = its?.value?.split(',');
                      return (
                        <li key={its?.field}>
                          <div>Weight & Volume details</div>
                          {weightVolumePairs?.map((pair, index) => (
                            <div key={pair}>
                              <div dangerouslySetInnerHTML={{ __html: `${index + 1}. ${pair}` }} />
                            </div>
                          ))}
                        </li>
                      );
                    }
                    return (
                      <li key={its?.field}>
                        <div dangerouslySetInnerHTML={{ __html: its?.msg }} />
                      </li>
                    );
                  }
                  return null;
                })}
              </ul>
            </>
          ) : null}
          {data?.messageDetail?.createdAt && (
            <p className="notification-date">
              {moment(data?.messageDetail?.createdAt).format('DD-MMMM-YYYY')} at {moment(data?.messageDetail?.createdAt).format('HH:mm')}
            </p>
          )}
          {data?.messageDetail?.footer && <p className="notification-footer-text">{shortenandHoverString(data?.messageDetail?.footer, 40)}</p>}
          {checkArray(memoizedHistory) && (
            <p className="notification-other">
              {!isExpand ? `+${memoizedHistory?.length} other Tasks` : ''}
              <button aria-label="arrow" type="button" onClick={(e) => props?.toggleItem(data?.messageDetail?._id, e)}>
                <span className={`icon-new-down-arrow-icon ${isExpand ? 'icon-inverse' : ''}`} />
              </button>
            </p>
          )}
        </div>
        <button type="button" className="notification-close-btn" aria-label="Close" onClick={(e) => addToReadingList(data, true, e)}>
          <span className="icon-close" />
        </button>
      </li>
    </ul>
  );
}

export default UserNotificationItem;
